import React from 'react';
import { 
  Box, 
  Button, 
  Grid, 
  Typography, 
  Paper, 
  Card, 
  CardContent,
  Chip,
  Fade
} from '@mui/material';
import { 
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  HelpOutline as UnansweredIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';

export default function ResultsGrid({ questions, userAnswers, onQuestionSelect }) {

  const getResult = (question, userAnswer) => {
    const isAnswered = userAnswer !== undefined && userAnswer !== null;
    if (!isAnswered) {
      return 'unanswered';
    }
    return userAnswer === question.correct_choice ? 'correct' : 'incorrect';
  };

  const getButtonStyle = (result, index) => {
    const baseStyle = {
      minWidth: '48px', 
      height: '48px',
      borderRadius: 2,
      fontWeight: 'bold',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 4
      }
    };

    switch (result) {
      case 'correct':
        return {
          ...baseStyle,
          background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
          color: 'white',
          boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
          '&:hover': {
            ...baseStyle['&:hover'],
            background: 'linear-gradient(45deg, #388E3C 30%, #689F38 90%)',
            boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)'
          }
        };
      case 'incorrect':
        return {
          ...baseStyle,
          background: 'linear-gradient(45deg, #f44336 30%, #ff5722 90%)',
          color: 'white',
          boxShadow: '0 4px 15px rgba(244, 67, 54, 0.3)',
          '&:hover': {
            ...baseStyle['&:hover'],
            background: 'linear-gradient(45deg, #d32f2f 30%, #f57c00 90%)',
            boxShadow: '0 6px 20px rgba(244, 67, 54, 0.4)'
          }
        };
      default: // unanswered
        return {
          ...baseStyle,
          background: 'linear-gradient(45deg, #9e9e9e 30%, #bdbdbd 90%)',
          color: 'white',
          boxShadow: '0 4px 15px rgba(158, 158, 158, 0.3)',
          '&:hover': {
            ...baseStyle['&:hover'],
            background: 'linear-gradient(45deg, #757575 30%, #9e9e9e 90%)',
            boxShadow: '0 6px 20px rgba(158, 158, 158, 0.4)'
          }
        };
    }
  };

  // 통계 계산
  const correctCount = questions.filter(q => getResult(q, userAnswers[q.id]) === 'correct').length;
  const incorrectCount = questions.filter(q => getResult(q, userAnswers[q.id]) === 'incorrect').length;
  const unansweredCount = questions.filter(q => getResult(q, userAnswers[q.id]) === 'unanswered').length;
  const totalQuestions = questions.length;
  const accuracy = totalQuestions > 0 ? ((correctCount / totalQuestions) * 100).toFixed(1) : 0;

  return (
    <Fade in timeout={800}>
      <Card 
        sx={{ 
          borderRadius: 3, 
          boxShadow: 4,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* 헤더 */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              📊 결과 분석
            </Typography>
          </Box>

          {/* 통계 칩들 */}
          <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Chip
              icon={<CheckIcon />}
              label={`정답: ${correctCount}문제`}
              color="success"
              variant="filled"
              sx={{ 
                fontSize: '1rem', 
                fontWeight: 'bold',
                px: 2,
                py: 1,
                height: 'auto'
              }}
            />
            <Chip
              icon={<CancelIcon />}
              label={`오답: ${incorrectCount}문제`}
              color="error"
              variant="filled"
              sx={{ 
                fontSize: '1rem', 
                fontWeight: 'bold',
                px: 2,
                py: 1,
                height: 'auto'
              }}
            />
            <Chip
              icon={<UnansweredIcon />}
              label={`미제출: ${unansweredCount}문제`}
              color="default"
              variant="filled"
              sx={{ 
                fontSize: '1rem', 
                fontWeight: 'bold',
                px: 2,
                py: 1,
                height: 'auto'
              }}
            />
            <Chip
              label={`정답률: ${accuracy}%`}
              color="primary"
              variant="filled"
              sx={{ 
                fontSize: '1.1rem', 
                fontWeight: 'bold',
                px: 3,
                py: 1,
                height: 'auto',
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
              }}
            />
          </Box>

          {/* 안내 텍스트 */}
          <Box 
            sx={{ 
              textAlign: 'center', 
              mb: 3,
              p: 2,
              backgroundColor: 'rgba(33, 150, 243, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(33, 150, 243, 0.1)'
            }}
          >
            <Typography variant="body1" color="text.secondary" fontWeight="500">
              💡 문제 번호를 클릭하면 해당 문제로 바로 이동합니다
            </Typography>
          </Box>

          {/* 문제 번호 그리드 */}
          <Grid container spacing={2} justifyContent="center">
            {questions.map((q, index) => {
              const result = getResult(q, userAnswers[q.id]);
              const buttonStyle = getButtonStyle(result, index);

              return (
                <Grid item key={q.id}>
                  <Fade in timeout={600 + index * 20}>
                    <Button
                      onClick={() => onQuestionSelect(index)}
                      sx={buttonStyle}
                    >
                      {index + 1}
                    </Button>
                  </Fade>
                </Grid>
              );
            })}
          </Grid>

          {/* 범례 */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box 
                sx={{ 
                  width: 16, 
                  height: 16, 
                  borderRadius: 1,
                  background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)'
                }} 
              />
              <Typography variant="body2" color="text.secondary">정답</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box 
                sx={{ 
                  width: 16, 
                  height: 16, 
                  borderRadius: 1,
                  background: 'linear-gradient(45deg, #f44336 30%, #ff5722 90%)'
                }} 
              />
              <Typography variant="body2" color="text.secondary">오답</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box 
                sx={{ 
                  width: 16, 
                  height: 16, 
                  borderRadius: 1,
                  background: 'linear-gradient(45deg, #9e9e9e 30%, #bdbdbd 90%)'
                }} 
              />
              <Typography variant="body2" color="text.secondary">미제출</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
}; 